import * as React from 'react'
import { isFragment } from 'react-is'
import clsx from 'clsx'
import { Avatar, AvatarProps } from '../Avatar'
import { SxProps, sx as sxFunc } from '../sx'
import style from './index.module.scss'

export interface AvatarGroupClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the avatar elements. */
  avatar: string
}

export interface AvatarGroupProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'>,
    Pick<AvatarProps, 'size' | 'variant' | 'color'> {
  /**
   * The avatars to stack.
   */
  children?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<AvatarGroupClasses>
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   * @default div
   */
  Component?: React.ElementType
  /**
   * Max avatars to show before +x.
   * @default 5
   */
  max?: number
  /**
   * The extra props for the slot components.
   * You can override the existing props or add new ones.
   *
   * @default {}
   */
  slotProps?: {
    additionalAvatar?: AvatarProps
  }
  /**
   * Spacing between avatars.
   * @default true
   */
  stacked?: boolean
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The total number of avatars. Used for calculating the number of extra avatars.
   * @default children.length
   */
  total?: number
}

const sizeMapping = {
  exSmall: 24,
  small: 32,
  medium: 48,
  large: 64,
  exLarge: 80,
}

export const AvatarGroup = React.forwardRef<HTMLDivElement, AvatarGroupProps>(
  function AvatarGroup(props, ref) {
    const {
      children: childrenProp,
      className,
      classes,
      Component = 'div',
      max = 5,
      slotProps = {},
      stacked = true,
      size: sizeProp = 24,
      color = 'secondary',
      total,
      variant = 'circular',
      sx,
      ...other
    } = props
    let clampedMax = max < 2 ? 2 : max

    const children = React.Children.toArray(childrenProp).filter(child => {
      if (process.env.NODE_ENV !== 'production') {
        if (isFragment(child)) {
          console.error(
            [
              "The AvatarGroup component doesn't accept a Fragment as a child.",
              'Consider providing an array instead.',
            ].join('\n')
          )
        }
      }

      return React.isValidElement(child)
    })

    const totalAvatars = total || children.length

    if (totalAvatars === clampedMax) {
      clampedMax += 1
    }

    clampedMax = Math.min(totalAvatars + 1, clampedMax)

    const maxAvatars = Math.min(children.length, clampedMax - 1)
    const extraAvatars = Math.max(
      totalAvatars - clampedMax,
      totalAvatars - maxAvatars,
      0
    )

    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line
      React.useEffect(() => {
        if (typeof sizeProp === 'string') {
          console.warn(
            'Use numeric size instead, for setting the size of Avatar.\n' +
              'exSmall : 24 \n' +
              'small : 32 \n' +
              'medium : 48 \n' +
              'large : 64 \n' +
              'exLarge : 80 \n'
          )
        }
      }, [sizeProp])
    }

    const size = React.useMemo(() => {
      if (typeof sizeProp !== 'string') {
        return sizeProp
      } else if (typeof sizeProp === 'string') {
        return sizeMapping[sizeProp] ?? sizeProp
      }
      return sizeProp
    }, [sizeProp])

    return (
      <Component
        className={clsx(
          style.root,
          classes?.root,
          style[`size-${size}`],
          !stacked && style.unstacked,
          className,
          sxFunc(sx)
        )}
        ref={ref}
        {...other}
      >
        {extraAvatars ? (
          <Avatar
            variant={variant}
            {...slotProps.additionalAvatar}
            size={size as AvatarProps['size']}
            color={color}
            className={clsx(
              style.avatar,
              classes?.avatar,
              slotProps?.additionalAvatar?.className
            )}
          >
            +{extraAvatars}
          </Avatar>
        ) : null}
        {children
          .slice(0, maxAvatars)
          .reverse()
          .map(child => {
            // @ts-ignore
            return React.cloneElement(child, {
              className: clsx(
                // @ts-ignore
                child.props.className,
                style.avatar,
                classes?.avatar
              ),
              size: size,
              color: color,
              // @ts-ignore
              variant: child.props.variant || variant,
            })
          })}
      </Component>
    )
  }
)
